<script setup lang="ts">
import { computed, useAttrs } from 'vue'

// inheritAttrs: false because focus/blur don't bubble, so @focus/@blur/
// @keydown.enter listeners (SearchBar.vue's usage) must land on the inner
// <input>, not the root div. That means class doesn't auto-fall-through to
// root either (inheritAttrs: false disables ALL of it, class included) —
// so root class is bound explicitly here, and everything else in $attrs
// (the listeners, any other passthrough attrs) goes to the input.
defineOptions({ inheritAttrs: false })

const props = defineProps<{
  modelValue: string
  placeholder?: string
  name?: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: string]
  input: [value: string]
  clear: []
}>()

const attrs = useAttrs()
const inputAttrs = computed(() => {
  const { class: _class, style: _style, ...rest } = attrs
  return rest
})

function onInput(e: Event) {
  const value = (e.target as HTMLInputElement).value
  emit('update:modelValue', value)
  emit('input', value)
}

function onClear() {
  emit('update:modelValue', '')
  emit('clear')
}
</script>

<template>
  <div
    class="search-bar__input"
    :class="attrs.class"
    :style="attrs.style"
  >
    <div class="search-bar__input--search">
      <fa :icon="['fal', 'magnifying-glass']" />
    </div>
    <input
      class="search-bar__input--field input"
      type="text"
      :name="name || 'search'"
      autocomplete="off"
      :placeholder="placeholder || 'Search'"
      :value="modelValue"
      v-bind="inputAttrs"
      @input="onInput"
    />
    <button
      v-show="modelValue.length > 0"
      type="button"
      class="search-bar__input--cross"
      aria-label="Clear search"
      @click="onClear"
    >
      <fa :icon="['fal', 'xmark']" />
    </button>
  </div>
</template>

<style lang="scss">
// Dimensions match the global text-input rule in main.scss (var(--tap)
// height, --radius-sm corner, same border + focus ring) so the search
// bar reads as part of the same input family as every other field on
// the site, not a one-off pill. Global (unscoped) since consumers
// (SearchBar, premium change-maker search) reference these class names
// directly rather than via scoped attrs.
.search-bar__input {
  display: flex;
  align-items: center; // vertically centres icon, input, and cross as one row
  width: 100%;
  min-height: var(--tap);
  background-color: var(--color-white-1);
  border: 0.0625rem solid var(--color-grey-3);
  border-radius: var(--radius-sm);
  overflow: hidden;
  transition:
    border-color var(--dur-fast) var(--ease-out),
    box-shadow var(--dur-fast) var(--ease-out);
  &:focus-within {
    border-color: var(--color-pink-1);
    box-shadow: 0 0 0 0.1875rem var(--color-pink-3);
  }
  &--search,
  &--cross {
    display: flex;
    align-items: center;
    justify-content: center;
    align-self: stretch;
    padding: 0 0.9rem;
    background: transparent;
    border: 0;
    color: var(--color-grey-1);
    cursor: pointer;
    flex: 0 0 auto;
    svg {
      font-size: 1rem;
      width: 1rem;
      height: 1rem;
      color: var(--color-grey-1);
    }
  }
  &--field {
    flex: 1 1 auto;
    min-width: 0;
    // Reset every bit of chrome the global text-input rule tries to apply.
    background: transparent !important;
    border: 0 !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    padding: 0 0.25rem !important;
    min-height: auto !important;
    height: auto;
    // Explicit line-height keeps the caret + text vertically centred inside
    // the 44px wrapper (the global input rule would push it to the top).
    line-height: 1.5 !important;
    font-size: var(--text-sm);
    color: var(--color-grey-1);
    cursor: text;
    &:focus {
      outline: none;
      border-color: transparent !important;
      box-shadow: none !important;
    }
  }
}
</style>
